import { Box, CircularProgress, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CurrencyField } from '~/components/GenericForm/CurrencyField';
import { EnumSelect } from '~/components/GenericForm/EnumSelect';
import { AllowanceProposalProps } from '~/data/proposals';
import {
  CommonEmployeeProposalFormHandle,
  CommonEmployeeProposalFormProps,
  ProposalDataFormButtons,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { ProposalResponsibleHRSelect } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalResponsibleHrSelect';
import { useProposalFormImperativeHandle } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useProposalFormImperativeHandle';
import { useEmployeeDataForProposal } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/useEmployeeDataForProposal';

import { ScheduleEmploymentsDropdown } from '../Common/components/ScheduleEmploymentsDropdown';
import { useSxStyles } from '../Common/useStyles';

import { useAllowanceProposalForm } from './useAllowanceProposalForm';

type PropsType = CommonEmployeeProposalFormProps & {
  proposal: AllowanceProposalProps;
};

export const AllowanceProposalForm = React.forwardRef<
  CommonEmployeeProposalFormHandle,
  PropsType
>((props, ref) => {
  const { proposal, employeeId, isNewProposal, onProposalUpdate } = props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { currency } = useEmployeeDataForProposal({ proposal });
  const {
    form,
    enums: { allowanceTypes, eventFrequencies },
    responsibleHRSelector,
    isLoading,
    handleFormSubmit,
    handleFormReset,
    readOnlyMode,
    canSubmitForm,
    fieldProps,
  } = useAllowanceProposalForm({
    proposal,
    employeeId,
    onProposalUpdate,
  });
  const { control, formState } = form;

  useProposalFormImperativeHandle({ ref, handleFormSubmit });

  if (isLoading.responsibleHR || isLoading.commonFieldProps) {
    return <CircularProgress />;
  }

  return (
    <Box autoComplete="off" component="form" sx={sxStyles.form}>
      <Controller
        name={'employmentId'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <ScheduleEmploymentsDropdown
              value={value}
              onChange={onChange}
              error={error}
              readOnlyMode={readOnlyMode}
            />
          );
        }}
      />
      <Controller
        name={'allowanceType'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <EnumSelect
              FormControlProps={{ fullWidth: true, error: !!error?.message }}
              value={value}
              onChange={onChange}
              enumOptions={allowanceTypes}
              id={'allowanceType'}
              label={t('laAllowanceType')}
              required={true}
              error={!!error?.message}
              helperText={error?.message}
              disabled={readOnlyMode}
            />
          );
        }}
      />
      <Controller
        name={'allowanceFrequency'}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          return (
            <EnumSelect
              FormControlProps={{ fullWidth: true, error: !!error?.message }}
              value={value}
              onChange={onChange}
              enumOptions={eventFrequencies}
              id={'frequency'}
              label={t('laFrequency')}
              required={true}
              helperText={error && error.message}
              disabled={readOnlyMode}
            />
          );
        }}
      />
      <Box sx={sxStyles.dateRange}>
        <Controller
          name={'startDate'}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <DatePicker
                label={t('laStartDate')}
                value={value}
                onChange={onChange}
                disabled={readOnlyMode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required={true}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                )}
                {...fieldProps.datePickers.startDate}
              />
            );
          }}
        />
        <Box sx={{ pb: 3 }}>-</Box>
        <Controller
          name={'endDate'}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <DatePicker
                label={t('laEndDate')}
                value={value}
                onChange={onChange}
                disabled={readOnlyMode}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                )}
                {...fieldProps.datePickers.endDate}
              />
            );
          }}
        />
      </Box>
      <Box sx={sxStyles.fieldRow}>
        <Controller
          name={'allowanceAmount'}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <CurrencyField
                label={t('laAllowance')}
                value={value}
                onChange={onChange}
                currency={currency}
                error={!!error?.message}
                helperText={error?.message}
                disabled={readOnlyMode}
              />
            );
          }}
        />
        <Controller
          name={'taxableAmount'}
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <CurrencyField
                label={t('laTaxable')}
                value={value}
                onChange={onChange}
                currency={currency}
                error={!!error?.message}
                helperText={error?.message}
                disabled={readOnlyMode}
              />
            );
          }}
        />
      </Box>
      <Controller
        name={'allowanceInfo'}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <TextField
              label={t('laAllowanceInfo')}
              {...field}
              fullWidth={true}
              disabled={readOnlyMode}
              error={!!error?.message}
              helperText={error?.message}
            />
          );
        }}
      />
      <ProposalResponsibleHRSelect
        form={form}
        control={control}
        selectorSettings={responsibleHRSelector}
        disabled={readOnlyMode}
      />
      <ProposalDataFormButtons
        isNewProposal={isNewProposal}
        onFormSubmitWithDecision={handleFormSubmit}
        onFormReset={handleFormReset}
        formState={formState}
        readOnlyMode={readOnlyMode}
        submitDecisionAllowed={canSubmitForm}
      />
    </Box>
  );
});
